/**
 * Which packs a skipper's AREA means — the maths behind the provider rework
 * (fb-msn0j0jv-3, decisions of 10 Aug).
 *
 * The skipper thinks in areas: a box dragged over the water, or "along my
 * route, this wide". The packs are an implementation detail, so these helpers
 * translate an area into the set of packs that cover it, and answer the
 * running question the off-edge notice needs: does what we have downloaded
 * cover what is on screen.
 *
 * Pure functions over pack bounds, no store access, so the lot unit-tests in
 * node. Bounds are [w, s, e, n] lon/lat everywhere, matching ChartPack.
 */
import type { ChartPack } from './providers';
import { rhumbDistanceNm, distanceToRun } from '@/nav';

export type LonLatBox = [number, number, number, number];

/** Do two lon/lat boxes overlap at all. */
export function boxesOverlap(a: LonLatBox, b: LonLatBox): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

/** The packs whose bounds touch a dragged box, smallest first (cheap first). */
export function packsIntersecting(packs: ChartPack[], box: LonLatBox): ChartPack[] {
  return packs
    .filter((p) => boxesOverlap(p.bounds, box))
    .sort((a, b) => a.sizeBytes - b.sizeBytes);
}

/**
 * Distance from a point to a route LEG, nautical miles.
 *
 * Inside the leg's span it is the cross-track distance; beyond either end it
 * is the distance to the nearer endpoint, because a pack abeam of the middle
 * of a leg and a pack just past a waypoint are both "along the route" in the
 * sense a skipper means it.
 */
export function pointToLegNm(
  p: [number, number],
  a: [number, number],
  b: [number, number],
): number {
  const legNm = rhumbDistanceNm(a, b);
  if (legNm < 1e-6) return rhumbDistanceNm(p, a);
  const { xteNm, distanceToGoNm } = distanceToRun(p, a, b);
  const alongNm = legNm - distanceToGoNm;
  if (alongNm >= 0 && alongNm <= legNm) return Math.abs(xteNm);
  return Math.min(rhumbDistanceNm(p, a), rhumbDistanceNm(p, b));
}

/** The corners and centre of a box, the probe points for near-a-leg tests. */
function boxProbes(box: LonLatBox): [number, number][] {
  const [w, s, e, n] = box;
  return [
    [w, s],
    [e, s],
    [e, n],
    [w, n],
    [(w + e) / 2, (s + n) / 2],
  ];
}

/**
 * The packs within `widthNm` of a route, in route order of first relevance.
 *
 * A pack counts when a leg passes through its box (the crossing case), or when
 * any of its corners or centre lies within the width of a leg (the abeam
 * case). Probing corners rather than buffering polygons keeps this exact
 * enough for pack-sized regions while staying a page of arithmetic; a pack big
 * enough to defeat the probes is big enough to be caught by the crossing test.
 */
export function packsAlongRoute(
  packs: ChartPack[],
  waypoints: [number, number][],
  widthNm: number,
): ChartPack[] {
  if (waypoints.length < 2) return [];
  const picked: ChartPack[] = [];
  const taken = new Set<string>();
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i];
    const b = waypoints[i + 1];
    // The leg's own bbox, padded by the width, as the cheap first filter.
    const midLat = (a[1] + b[1]) / 2;
    const latPad = widthNm / 60;
    const lonPad = widthNm / (60 * Math.max(0.2, Math.cos((midLat * Math.PI) / 180)));
    const legBox: LonLatBox = [
      Math.min(a[0], b[0]) - lonPad,
      Math.min(a[1], b[1]) - latPad,
      Math.max(a[0], b[0]) + lonPad,
      Math.max(a[1], b[1]) + latPad,
    ];
    for (const pack of packs) {
      if (taken.has(pack.id)) continue;
      if (!boxesOverlap(pack.bounds, legBox)) continue;
      const near =
        legEndsInBox(a, b, pack.bounds) ||
        boxProbes(pack.bounds).some((pt) => pointToLegNm(pt, a, b) <= widthNm);
      if (near) {
        taken.add(pack.id);
        picked.push(pack);
      }
    }
  }
  return picked;
}

/** Does either end of the leg sit inside the box (the pack-crossing case). */
function legEndsInBox(a: [number, number], b: [number, number], box: LonLatBox): boolean {
  const inBox = (p: [number, number]) =>
    p[0] >= box[0] && p[0] <= box[2] && p[1] >= box[1] && p[1] <= box[3];
  return inBox(a) || inBox(b);
}

/**
 * Is a view fully covered by a set of downloaded boxes?
 *
 * Exact rectangle-union coverage is bookkeeping nobody needs: a 5-by-5 grid of
 * sample points answers the question the off-edge notice actually asks, with
 * the honest caveat that a sliver thinner than a fifth of the screen can slip
 * between probes. The notice is advisory; the chart itself never lies, because
 * what is not downloaded simply is not drawn.
 */
export function boxCoveredBy(view: LonLatBox, covers: LonLatBox[]): boolean {
  if (covers.length === 0) return false;
  const [w, s, e, n] = view;
  for (let i = 0; i <= 4; i++) {
    for (let j = 0; j <= 4; j++) {
      const lon = w + ((e - w) * i) / 4;
      const lat = s + ((n - s) * j) / 4;
      const hit = covers.some((c) => lon >= c[0] && lon <= c[2] && lat >= c[1] && lat <= c[3]);
      if (!hit) return false;
    }
  }
  return true;
}

/** Human size for a staged selection, e.g. "480 MB" or "1.2 GB". */
export function formatPackBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  return `${Math.max(1, Math.round(bytes / (1024 * 1024)))} MB`;
}
